// ac-info.ts — client-side render of the creature Evasion Class / Armor Class
// breakdown panel, reading from window.FateTide.casterCache (no DB ping on a hit).
//
// Replaces the PHP endpoint options/tools/encounter_creatures/encounter_creature_info/
// armor_class_info_inc.php. Data comes from caster_arr.computed.creature.ac:
//   ec / ac / mitigation → {total, sources} (provenance, ft_compute_persist)
//   max_body / natural_armor / armor / shield / dr → lean scalar totals
// Icons via iconMap(); HTML injected into #div_refresh_side_info.
//
// Consistent with the essence panel: rows render from the persisted source list,
// so zero contributors are naturally hidden (the old PHP always emitted the
// equipped-armor row even at +0 — now an armor/shield row appears only when it
// actually contributes AC; the item is still visible from the inventory/sheet).

import type { CachedCaster, SourceRow, AcPart } from './types';
import { renderSourceLineBlock, resolveLineLabel, resolveLineKind } from './source-line-block';
import { setActiveInfoPanel, paintActiveInfoPanel } from './active-panel';

// Catalog item ids used by the panel (stable; match the PHP icon_map / openItemInfo args).
const EQUIPPED_ARMOR_ICON_ID = '0B279A55-5102-D704-8A57-0C1FF4989DFA';
const DR_ITEM_ID = 'E1B8247A-0D5D-28B0-0ACA-A00896D1DA7F';
const MITIGATION_ITEM_ID = 'F2F3C4B8-122A-4C38-9F68-0020470A6AA9';
const EC_ITEM_ID = 'DB993D70-943F-2613-E714-63B5B9F9F160';
const AC_ITEM_ID = 'A7E84132-F035-4B28-8070-C3348608C538';

function num(v: unknown): number {
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isFinite(n) ? n : 0;
}

/** computed.creature.ac.<part> → total (handles bare-scalar and {total,…} shapes). */
function partTotal(part: AcPart | number | undefined): number {
    if (part === undefined || part === null) return 0;
    return (typeof part === 'object') ? num(part.total) : num(part);
}

function partSources(part: AcPart | number | undefined): SourceRow[] {
    return (part && typeof part === 'object' && Array.isArray(part.sources)) ? part.sources : [];
}

/** A clickable item badge (mirrors the PHP openItemInfo armor/shield name badge). */
function itemNameBadge(label: string, href: string, badgeClass = 'bg-dark border'): string {
    if (label === '') return '';
    const safe = escapeHtml(label);
    return (href !== '' && checkIfValidUUID(href))
        ? `<br /><span class="badge ${badgeClass}" role="button" onclick="openItemInfo('${escapeHtml(href)}');">${safe}</span>`
        : `<br /><span class="badge ${badgeClass}">${safe}</span>`;
}

/** Simple "icon + label / value badge" table row. */
function badgeRow(iconHtml: string, labelHtml: string, value: number, badgeClass: string): string {
    return `<tr>
        <th scope="row" class="align-middle">${iconHtml}${labelHtml}</th>
        <td class="text-center"><h3 class="mb-0"><span class="badge ${badgeClass}">${value}</span></h3></td>
    </tr>`;
}

/** A multi-line source block row (magical / non-magical AC modifiers). */
function sourceBlockRow(iconSlug: string, label: string, lines: SourceRow[], caster: CachedCaster): string {
    const total = lines.reduce((sum, ln) => sum + Number(ln.value ?? 0), 0);
    return `<tr>
        <th scope="row" class="align-middle">${iconMap(iconSlug)} ${escapeHtml(label)}</th>
        <td class="text-center align-middle">${renderSourceLineBlock(lines, iconSlug, total, 'bg-secondary', caster)}</td>
    </tr>`;
}

function renderEcTable(caster: CachedCaster, cluster: Record<string, AcPart | number>): string {
    const effectiveBody = partTotal(cluster.max_body);
    const ecTotal = partTotal(cluster.ec);
    const sheetBody = num(ftLayerValue(caster, 'body'));

    let capNote = '';
    if (sheetBody !== effectiveBody) {
        capNote = `<div class="small text-muted mt-2 text-start border rounded p-2">
            <strong>Body capped for EC/AC.</strong>
            Your character&rsquo;s Body is <strong>${sheetBody}</strong>,
            but only <strong>${effectiveBody}</strong> counts toward Evasion Class and Armor Class.
            Equipped armor or natural armor is limiting how much Body applies here.
        </div>`;
    }

    return `<table class="table border">
        <thead><tr><th scope="col" colspan="2" class="table-info align-middle">${iconMap('evasion_class')} Evasion Class</th></tr></thead>
        <tbody>
            <tr><th scope="row" class="align-middle">Base:</th><td class="text-center"><h3 class="mb-0"><span class="badge bg-info">10</span></h3></td></tr>
            <tr>
                <th scope="row" class="align-middle">${iconMap('body')} Body:</th>
                <td class="text-center"><h3 class="mb-0"><span class="badge bg-warning">${effectiveBody}</span></h3>${capNote}</td>
            </tr>
        </tbody>
        <tfoot><tr><th scope="row" class="align-middle">Evasion Class Total:</th><td class="text-center"><h3 class="mb-0"><span class="badge bg-dark">${ecTotal}</span></h3></td></tr></tfoot>
    </table>`;
}

function renderAcTable(caster: CachedCaster, cluster: Record<string, AcPart | number>): string {
    const effectiveBody = partTotal(cluster.max_body);
    const acTotal = partTotal(cluster.ac);
    const dr = partTotal(cluster.dr);

    const byCat: Record<string, SourceRow[]> = {};
    for (const r of partSources(cluster.ac)) {
        // ROUND-6 #3: `category` is elided at the store point when it equals `kind`;
        // ROUND-9 #5: `kind` is elided when it equals the catalog item_type of href_id —
        // derive both back through resolveLineKind.
        (byCat[String(r.category ?? resolveLineKind(caster, r))] ??= []).push(r);
    }
    const first = (cat: string): SourceRow | undefined => (byCat[cat] ?? [])[0];
    const byKind = (cat: string, kind: string): SourceRow | undefined =>
        (byCat[cat] ?? []).find(r => resolveLineKind(caster, r) === kind);

    let rows = '';
    // Base + Body (Body uses the capped value; same as Evasion Class).
    rows += `<tr><th scope="row" class="align-middle">Base:</th><td class="text-center"><h3 class="mb-0"><span class="badge bg-info">10</span></h3></td></tr>`;
    if (effectiveBody !== 0) {
        rows += badgeRow(iconMap('body'), ' Body (defense; same cap as Evasion Class):', effectiveBody, 'bg-warning');
    }

    const natural = first('natural_armor');
    if (natural) {
        rows += badgeRow(iconMap('natural_armor'), ' Natural armor AC:', Number(natural.value ?? 0), 'bg-primary');
    }

    const armor = byKind('inventory_equipped', 'armor');
    if (armor) {
        const armorLabel = String(armor.label ?? resolveLineLabel(caster, String(armor.href_id ?? '')));
        const lbl = ` Equipped Armor AC:${itemNameBadge(armorLabel, String(armor.href_id ?? ''))}`;
        rows += badgeRow(iconMap(EQUIPPED_ARMOR_ICON_ID), lbl, Number(armor.value ?? 0), 'bg-primary');
    }

    const shield = byKind('inventory_equipped', 'shield');
    if (shield) {
        const shieldLabel = String(shield.label ?? resolveLineLabel(caster, String(shield.href_id ?? '')));
        const lbl = ` AC from Shield:${itemNameBadge(shieldLabel, String(shield.href_id ?? ''), 'bg-dark')}`;
        rows += badgeRow(iconMap('shield'), lbl, Number(shield.value ?? 0), 'bg-warning');
    }

    // A plain flat value is one anonymous "Other" row; when a status delivers ac_mod_other the rows name each
    // status (+ an "Other" remainder) — ft_ac_mod_status_source_rows — and render as an attributed block.
    const otherRows = byCat['other'] ?? [];
    if (otherRows.length > 1 || otherRows.some(r => String(r.href_id ?? '') !== '')) {
        rows += sourceBlockRow('ac_mod_other', 'AC Modifer Other:', otherRows, caster);
    } else if (otherRows.length === 1) {
        rows += badgeRow(iconMap('ac_mod_other'), ' AC Modifer Other:', Number(otherRows[0]!.value ?? 0), 'bg-warning');
    }

    const magical = byCat['ac_mod_magical'] ?? [];
    if (magical.length > 0) {
        rows += sourceBlockRow('ac_mod_magical', 'AC Modifer Magical:', magical, caster);
    }

    const nonMagical = byCat['ac_mod_non_magical'] ?? [];
    if (nonMagical.length > 0) {
        rows += sourceBlockRow('ac_mod_non_magical', 'AC Modifer Non-Magical:', nonMagical, caster);
    }

    const env = first('ac_mod_environmental');
    if (env) {
        rows += badgeRow(iconMap('ac_mod_environmental'), ' AC Modifer Environmental:', Number(env.value ?? 0), 'bg-warning');
    }

    // Footer: AC total, Damage Reduction, Damage Mitigation block.
    let foot = `<tr><th scope="row" class="align-middle">Armor Class Total:</th><td class="text-center"><h3 class="mb-0"><span class="badge bg-dark">${acTotal}</span></h3></td></tr>`;
    foot += `<tr>
        <th scope="row" class="align-middle"><span role="button" onclick="openItemInfo('${DR_ITEM_ID}');">${iconMap('damage_reduction')} Damage Reduction:</span></th>
        <td class="text-center"><h3 class="mb-0"><span class="badge bg-danger">${dr}</span></h3></td>
    </tr>`;

    const mitigationTotal = partTotal(cluster.mitigation);
    if (mitigationTotal > 0) {
        const mitLines = partSources(cluster.mitigation);
        foot += `<tr>
            <th scope="row" class="align-middle"><span role="button" onclick="openItemInfo('${MITIGATION_ITEM_ID}');">${iconMap('damage_mitigation')} Damage Mitigation:</span></th>
            <td class="text-center align-middle">${renderSourceLineBlock(mitLines, 'damage_mitigation', mitigationTotal, 'bg-warning text-dark', caster)}</td>
        </tr>`;
    }

    return `<table class="table border">
        <thead>
            <tr><th scope="col" colspan="2" class="table-primary align-middle">${iconMap('armor_class')} Armor Class</th></tr>
            <tr><th scope="col" colspan="2" class="fw-normal small text-start text-muted">
                <strong>How AC adds up:</strong> 10 + Body (capped, same value as in Evasion Class) + armor, shield, natural armor, and the other modifier rows in this table.
                Do not read the rows below as 10 + armor + shield + misc only&mdash;<strong>Body is part of the total</strong> before those equipment bonuses.
            </th></tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>${foot}</tfoot>
    </table>`;
}

/** Static explanatory footer (verbatim from the PHP panel). */
function renderExplainer(): string {
    return `
    <div class="text-left p-2 mb-3 border rounded small">
        <p class="mb-0"><strong>Evasion Class (EC)</strong> is <strong>10 + Body</strong> (the value in the table, which can be less than your sheet Body if armor or natural armor caps it). It is your dodge. If the attack roll is <strong>below your EC</strong>, the attack misses.</p>
    </div>
    <div class="text-left p-2 mb-3 border rounded small">
        <p class="mb-0"><strong>Armor Class (AC)</strong> is <strong>10 + the same Body + armor, shield, natural armor, and the other rows in the AC table</strong>. A roll <strong>at or above your EC</strong> can hit. Rolls <strong>between EC and AC</strong> still hit, but damage is often reduced. <strong>Damage reduction (DR) = AC &minus; 10</strong> (how much your protections absorb from the damage roll).</p>
    </div>
    <div class="text-left p-2 mb-3 border rounded small">
        <p class="mb-0"><strong>Example:</strong> EC 12, AC 18. Roll &lt; 12: miss. Roll 12&ndash;17: hit, reduced (DR 8, since 18 &minus; 10 = 8). Roll &ge; 18: full damage (per usual for your rules). 5 damage vs DR 8 &rarr; 0; 15 damage &rarr; 7. Natural 20 still behaves as a crit per your table.</p>
    </div>
    <div class="text-left p-2 mb-3 border rounded small">
        <p class="mb-0">Many games treat hit/miss as all-or-nothing. Janus uses two linked scores&mdash;<a href="${escapeHtml(BASE_URL + 'item.php?id=' + EC_ITEM_ID)}" class="link-primary" target="_blank" rel="noopener noreferrer">Evasion Class (EC)</a> and <a href="${escapeHtml(BASE_URL + 'item.php?id=' + AC_ITEM_ID)}" class="link-primary" target="_blank" rel="noopener noreferrer">Armor Class (AC)</a>&mdash;in the spirit of systems such as Dungeon Coach and Rexx, so you get chip damage, tanks, and evasive play without the math exploding. At very high AC, attacks can still feel threatening instead of whiffing forever.</p>
    </div>`;
}

function renderArmorClassPanel(caster: CachedCaster): string {
    const cluster = (caster.computed?.creature?.ac as Record<string, AcPart | number> | undefined) ?? {};
    return renderEcTable(caster, cluster) + renderAcTable(caster, cluster) + renderExplainer();
}

/**
 * Entry point. Renders the EC/AC breakdown for a caster into #targetId from the
 * client cache (fetch-on-miss). Repointed onclick: the AC bar in
 * build_v_mana_ecac_luck_bars_inc.php.
 */
export async function renderArmorClassInfoPanel(
    casterId: string,
    targetId = 'div_refresh_side_info',
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors; fall through to the miss guard.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }

    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    setActiveInfoPanel(casterId, targetId, renderArmorClassPanel);
    paintActiveInfoPanel(caster);
}
